/**
 * When a contact's materialized steps fall due.
 *
 * Named for the concern rather than the component, like enrollment.ts beside
 * LifecyclePanel.tsx and sequenceRules.ts beside Sequences.tsx. There is no
 * screen for this yet; it is the arithmetic `enroll_contact()` performs when it
 * copies a template's steps onto an enrollment as `enrollment_steps` rows, and
 * the arithmetic a resume performs on what is left of them.
 *
 * WHY THE RULES ARE HERE AND NOT IN SQL
 * -------------------------------------
 * The dates decide what a person receives and in what order, and every way
 * they can be wrong is SILENT: a cadence a week short, a follow-up landing
 * before the thing it follows up on, a paused plan bunching against itself on
 * resume. None of that raises, none of it is visible from a screen, and a
 * `date_trunc` argument buried in a plpgsql function is provable only by
 * running the function. The `marketing` vitest project runs plain `.ts` in
 * Node at ~1s, and 074 can then call something that has already been argued
 * with. Same case sendClient.ts makes about the batch loop.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE THREE THINGS THIS FILE IS FOR
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 1. `offset_days` IS RELATIVE TO THE PREVIOUS STEP, SO THE OFFSETS
 *    ACCUMULATE.
 *
 *    052 chose that deliberately -- "days after the PREVIOUS step, not after
 *    enrollment, so inserting a step in the middle does not silently
 *    reschedule everything after it". The consequence here is that a template
 *    of [0, 7, 7] is days 0, 7 and 14. Read straight, it is days 0, 7 and 7,
 *    and the error compounds with every step: the third message lands a week
 *    early, on top of the second, and nothing anywhere reports it. That is
 *    the same sum stepSchedule() computes for the sequence screen -- and the
 *    reason it is computed rather than eyeballed is written up there.
 *
 * 2. THE JITTER MOVES TO ENROLL TIME, AND IS APPLIED ONCE.
 *
 *    Today the generator adds +/-1 day at each advance, so a weekly cadence
 *    does not always land on a Wednesday and read to the recipient as
 *    automation. Applying it once, when `due_at` is computed, has the same
 *    anti-robotic effect and makes the whole jittered plan visible in
 *    advance -- which is what lets a screen answer "when will they next hear
 *    from us?" without simulating the rest of the template.
 *
 *    PER STEP, NOT PER PLAN. Shifting the whole plan by one day is not jitter
 *    at all: a weekly cadence offset by a day is still weekly, still lands on
 *    the same weekday for ever, and still reads as automation.
 *
 *    NOTHING AT CUMULATIVE OFFSET 0 IS JITTERED. THIS IS THE ONLY PLACE THAT
 *    RULE NOW LIVES. enrollment.ts's firstActionAt() used to hold it, in the
 *    form "no jitter on the FIRST step", on the argument that somebody just
 *    chose this moment on purpose and moving it a day either way would be the
 *    software second-guessing a decision it was not asked about. That argument
 *    is right, and it is about the MOMENT rather than about a row's position --
 *    which matters here because under materialization a plan can be
 *    reordered, inserted into or hand-built, so "the first step" names a
 *    position that stops meaning what it says the moment somebody adds a step
 *    in front of it. Stated as a rule about the cumulative offset it survives
 *    all three: whatever ends up at offset 0 is the DAY the human picked,
 *    whatever its step_no, and no random number moves it off that day.
 *
 *    firstActionAt() and newEnrollment() were deleted when enroll_contact()
 *    landed and startEnrollment() began composing the whole plan through this
 *    file. Nothing of theirs was dropped: the sentence above is their
 *    reasoning, restated in the terms materialization made necessary.
 *
 * 3. EVERY COMPOSED DATE IS THE SAME LOCAL INSTANT, ON A WEEKDAY (crm#14).
 *
 *    Until 2026-09-06 the arithmetic here started from `enrolledAt.getTime()`
 *    and added fixed 24-hour spans, so every step in a plan landed at the
 *    clock time somebody happened to press Enroll. The one live enrollment
 *    shows it: all five steps at 17:55:20.713+00, which is 13:55 Eastern and
 *    fine BY ACCIDENT. Enrolling at 9pm Eastern would have dated the whole
 *    plan at 02:00 UTC -- late evening of the PREVIOUS local day, which is
 *    the day the browser would then print and the day `isOverdue()` would
 *    compare against.
 *
 *    And nothing anywhere rolled a Saturday forward. `jitterDays()` returns
 *    whole days, so it could move a step ONTO a weekend but never off one.
 *
 *    So the composed instant is now derived, not inherited: the local
 *    calendar day the step belongs to, rolled forward off Saturday and
 *    Sunday, at DUE_HOUR_LOCAL in SCHEDULE_TIME_ZONE. Read the two constants
 *    below before changing either -- the hour in particular is pinned to a
 *    measurement of when the nurture tick actually arrives, not to taste.
 *
 *    EXISTING ROWS ARE NOT RETRO-FITTED. This function composes; it does not
 *    migrate. The five steps on enrollment 3684 keep 17:55:20.713+00 for
 *    ever unless somebody retimes them by hand through planRules.ts. That is
 *    deliberate: a step is a promise about a person, and rewriting dates
 *    under a plan somebody is already walking is the kind of silent
 *    rescheduling this file exists to argue against.
 * ══════════════════════════════════════════════════════════════════════════
 */

/**
 * THE ZONE THE SCHEDULE IS EXPRESSED IN.
 *
 * A CONSTANT, NOT THE COMPOSING MACHINE'S ZONE. `Intl.DateTimeFormat()
 * .resolvedOptions().timeZone` would compose a different plan depending on
 * where the laptop was when somebody clicked Enroll -- so the same template,
 * the same contact and the same day would produce different `due_at` values
 * from a phone in a different state, and nothing would report it. The
 * business has one working day, so the schedule has one zone.
 *
 * EASTERN IS EVIDENCED. A task whose `due_date` is stored as
 * 2026-08-31T00:00:00Z renders in the user's own browser as "8/30/2026,
 * 8:00:00 PM" -- UTC-4, which in August is America/New_York. The nurture
 * workflow's own header says the same thing from the other side: its cron is
 * described in Eastern ("TARGET: 06:00 Eastern (10:00 UTC in EDT)").
 *
 * A NAMED ZONE, NEVER A FIXED OFFSET. America/New_York is UTC-4 in summer and
 * UTC-5 in winter. Anything built on a constant -4 is correct today and wrong
 * from the first Sunday in November, in the direction that moves work by an
 * hour and, near midnight, by a day. Every conversion below goes through
 * Intl's tz database.
 */
export const SCHEDULE_TIME_ZONE = "America/New_York";

/**
 * The local hour a composed step falls due. 5 == 05:00 Eastern.
 *
 * ** THIS NUMBER IS A PRODUCT DECISION AND IT IS PINNED TO A MEASUREMENT.
 *    IT IS THE FIRST THING TO CHANGE IF THE TICK MOVES. **
 *
 * crm#14 suggested 9am, and 9am is what a person would pick. It is wrong
 * here, and the reason is worth the paragraph because it is not visible from
 * this file.
 *
 * `private.due_enrollment_steps()` selects `es.due_at <= now()`, and it is
 * run ONCE A DAY by .github/workflows/nurture.yml. So a step is turned into a
 * task by the first tick at or after its `due_at` -- and if `due_at` is later
 * in the day than the tick, the step waits for TOMORROW's tick. The task then
 * arrives carrying yesterday's date, and `isOverdue()` (local midnight) says
 * it is late. Not once: every step, every plan, for ever, silently.
 *
 * Measured 2026-09-06, `gh run list -R dhew10/barrel-scout-crm --workflow
 * nurture.yml`, the last three scheduled starts:
 *
 *     2026-09-06T09:47:27Z    2026-09-05T09:32:57Z    2026-09-04T10:03:10Z
 *
 * i.e. 05:32 to 06:03 Eastern -- the workflow's stated target, reached by
 * setting a "0 6 * * *" cron four hours early to absorb GitHub's queueing
 * lag. 09:00 Eastern is 13:00 UTC, more than three hours AFTER every one of
 * those runs, so 9am would have made every task a day late. 05:00 Eastern is
 * 09:00 UTC, ahead of the earliest observed start by 32 minutes.
 *
 * The consequence, stated plainly so nobody has to rediscover it: UNDER A
 * DAILY PRE-DAWN TICK, `due_at` CANNOT BE A MID-MORNING HOUR. The tick runs
 * before the working day on purpose ("so the tasks are already there when the
 * day starts"), and a deadline can only be caught by a sweep that runs after
 * it. The hour here is therefore the START of the local day, not the hour a
 * human is expected to act.
 *
 * WHY NOT EARLIER STILL. The other three constraints:
 *
 *   - It must be comfortably AFTER local midnight. `isOverdue()` in
 *     tasks/tasksPredicate.ts is `new Date(due) < startOfToday()`, local
 *     midnight, so a step composed for today must not sit on or before it.
 *     05:00 clears it by five hours; 00:00 would clear it only by exact
 *     equality, which is a coin toss away from the bug crm#14 warns about.
 *   - It must be a wall-clock hour that HAPPENS EXACTLY ONCE ON EVERY DAY OF
 *     THE YEAR in this zone. 02:00 does not exist on the spring-forward
 *     Sunday and 01:00 happens twice on the fall-back Sunday; both would make
 *     a composed instant ambiguous or invented. 05:00 is clear of both.
 *   - The margin above is 32 minutes against an UNDOCUMENTED GitHub
 *     behaviour, exactly as the cron itself is. If the observed start time
 *     moves EARLIER than 05:00 Eastern, every composed step becomes a task a
 *     day late and reads overdue on arrival. Re-measure with the command
 *     above and lower this number, or move the cron.
 */
export const DUE_HOUR_LOCAL = 5;

/* ── civil dates in SCHEDULE_TIME_ZONE ─────────────────────────────────── */

/** A calendar date in SCHEDULE_TIME_ZONE. `month` is 1-12, as a person writes it. */
export type CivilDate = { year: number; month: number; day: number };

/** A civil date plus the clock, and the weekday it falls on. 0 = Sunday. */
export type WallClock = CivilDate & {
  hour: number;
  minute: number;
  second: number;
  weekday: number;
};

/**
 * Constructed once, at module load, and deliberately not inside a function.
 *
 * If the runtime has no tz database for this zone, Intl throws RangeError
 * HERE -- on import, in every test, loudly -- rather than silently falling
 * back to UTC inside a date nobody looks at. `hourCycle: "h23"` because
 * en-US with `hour12: false` can report midnight as hour 24.
 */
const ZONE_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: SCHEDULE_TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** What a clock in SCHEDULE_TIME_ZONE reads at this instant. */
export function localWallClock(instant: Date): WallClock {
  const parts: Record<string, number> = {};
  for (const part of ZONE_FORMAT.formatToParts(instant)) {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  }
  const date = { year: parts.year, month: parts.month, day: parts.day };
  return {
    ...date,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
    weekday: weekdayOf(date),
  };
}

/**
 * The zone's offset from UTC in ms at a given instant (zone minus UTC, so
 * negative in the Americas). Read from the tz database through Intl rather
 * than assumed, which is the whole reason DST is not a bug here.
 */
function offsetAt(instant: Date): number {
  const w = localWallClock(instant);
  const asIfUtc = Date.UTC(
    w.year,
    w.month - 1,
    w.day,
    w.hour,
    w.minute,
    w.second,
  );
  // formatToParts has no milliseconds, so compare against the whole second.
  return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** 0 = Sunday .. 6 = Saturday, for a civil date. No instant, so no zone. */
function weekdayOf(date: CivilDate): number {
  return new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
}

/**
 * Calendar-day arithmetic on a civil date, which is where day arithmetic
 * belongs.
 *
 * The old code added fixed 24-hour spans to an INSTANT and its comment
 * argued that an hour of DST drift "can only move a task by an hour on two
 * days of the year". True of the hour, false of the DAY: a plan composed at
 * 00:30 local, plus 24 hours, lands at 23:30 the SAME local day across the
 * fall-back Sunday, and two steps then share a day. Adding to the civil date
 * cannot do that, because a civil date has no hours in it.
 */
function addDays(date: CivilDate, days: number): CivilDate {
  const moved = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return {
    year: moved.getUTCFullYear(),
    month: moved.getUTCMonth() + 1,
    day: moved.getUTCDate(),
  };
}

/**
 * Saturday and Sunday roll FORWARD to Monday; a weekday is left alone.
 *
 * Forward rather than back for the same reason shiftAfterPause() refuses a
 * negative delta: rolling Saturday back to Friday makes work due EARLIER than
 * the template asked for, and a plan that quietly becomes more urgent than
 * anybody scheduled is the failure, not the fix.
 */
function rollOffWeekend(date: CivilDate): CivilDate {
  const weekday = weekdayOf(date);
  if (weekday === 6) return addDays(date, 2); // Saturday -> Monday
  if (weekday === 0) return addDays(date, 1); // Sunday   -> Monday
  return date;
}

/**
 * The instant DUE_HOUR_LOCAL reads on a given civil date, rolled off the
 * weekend. THE ONE PLACE THE POLICY IS APPLIED; everything below calls this.
 *
 * ONE OFFSET LOOKUP, NOT THE USUAL TWO, AND THE REASON IS THE WEEKEND ROLL.
 *
 * The textbook wall-clock-to-instant conversion probes the offset twice: the
 * first probe is taken hours away from the instant it is trying to find, so
 * on a day when the offset CHANGES the two can sit either side of the change.
 * A second probe, at the candidate instant, settles it.
 *
 * Here the day the offset changes cannot arrive. In the United States both
 * transitions fall on a SUNDAY, and rollOffWeekend() has already moved every
 * Sunday to the Monday before this line runs -- so `onWeekday` is never a
 * transition date, the offset is constant across the whole of it, and one
 * probe is exact. A second pass was written first and its mutation came back
 * GREEN, because nothing can reach it; it was deleted rather than kept as
 * insurance.
 *
 * WHAT HOLDS THAT UP, since the argument is now load-bearing: "never composes
 * a step onto a Saturday or a Sunday" and its resume twin, plus a sweep of
 * every day from 2024 to 2032 asserting the composed instant reads
 * DUE_HOUR_LOCAL locally -- eighteen real transitions, both directions.
 * Anything that lets a Sunday through fails those before it gets here.
 */
function dueInstantOn(date: CivilDate): Date {
  const onWeekday = rollOffWeekend(date);
  const wall = Date.UTC(
    onWeekday.year,
    onWeekday.month - 1,
    onWeekday.day,
    DUE_HOUR_LOCAL,
  );
  return new Date(wall - offsetAt(new Date(wall)));
}

/**
 * Snap an instant onto the business instant of the local day it falls on.
 *
 * Exported because it is the policy, and a second copy of it elsewhere is
 * exactly the drift this file's header argues about. Callers that already
 * hold a civil day never need it; callers holding an instant (a resume, say)
 * do.
 */
export function businessDueAt(instant: Date): Date {
  const w = localWallClock(instant);
  return dueInstantOn({ year: w.year, month: w.month, day: w.day });
}

/**
 * Jitter, applied ONCE at enroll time rather than per advance.
 *
 * `rand` IS A PARAMETER, for the reason cancelOutbound() takes its RPC as
 * one: against Math.random() every assertion about this is either flaky or
 * vacuous. "Stays within +/-1" passes by accident two thirds of the time, and
 * "the adverse case is clamped" cannot even be stated, because the adverse
 * case cannot be asked for.
 *
 * The clamp on `floor` is not decoration. A substituted `rand` returning
 * exactly 1 -- or a caller passing something that is not Math.random at all --
 * would otherwise yield +2, and the bound this function exists to hold is the
 * one thing nothing downstream re-checks.
 */
export function jitterDays(rand: () => number = Math.random): -1 | 0 | 1 {
  const bucket = Math.floor((rand() || 0) * 3);
  if (bucket <= 0) return -1;
  if (bucket >= 2) return 1;
  return 0;
}

/** A template step, reduced to the two fields the dates are computed from. */
export type OffsetStep = { step_no: number; offset_days: number };

/** One materialized step: the number it keeps, and the date it lands on. */
export type MaterializedStep = { step_no: number; due_at: Date };

/**
 * Turn a template's relative offsets into real dates, jittered, on weekdays.
 *
 * SORTED BY step_no RATHER THAN TRUSTING ARRIVAL ORDER. PostgREST returns
 * whatever the planner produced unless asked to sort, and this function's
 * whole job is a running total -- fed rows in the wrong order it would
 * produce a plan that is wrong in a way no single date looks wrong.
 *
 * The running total is over the INTENDED days, not the jittered ones. Adding
 * each step's jitter to the previous step's jittered day would let the error
 * accumulate: eight steps of -1 would pull the last one a week early, which
 * is a rescheduling rather than a jitter.
 *
 * JITTER FIRST, THEN THE WEEKEND ROLL, AND THE ORDER IS THE POINT. Rolling
 * Saturday to Monday and THEN jittering by -1 puts the step back on Sunday --
 * the roll undone by the very thing it was protecting against, in a third of
 * cases, silently. Jittering the intended day and rolling whatever comes out
 * is the only order in which "no composed step is due at a weekend" is a
 * property rather than a tendency. The price is that jitter is not uniform
 * near a weekend -- a Friday step jittered +1 lands on Monday, same as +3
 * would -- and that is the right thing to give up: the jitter exists to stop
 * a cadence reading as a machine, and no human sends Saturday mail.
 *
 * The clamp is the other half of that. Jitter is allowed to reorder nothing:
 * a step dragged in front of its predecessor means the person reads the
 * follow-up before the thing it follows up on, and a step dragged in front of
 * the day of enrollment is due on arrival, so the wait the template asked for
 * never happens. Both cases resolve to "no earlier than", which is why the
 * clamp is a single `max` against whatever came before. It is applied to the
 * SNAPPED instants, which is sound because the snap is monotonic: a later
 * instant never snaps to an earlier business day.
 *
 * WHAT THE CLAMP'S FLOOR NOW IS, SAID OUT LOUD BECAUSE IT CHANGED. It used to
 * be `enrolledAt` exactly; it is now the business instant of the day
 * `enrolledAt` falls on, which for an afternoon enrollment is EARLIER than
 * the click. That is the whole point of crm#14 -- the plan must not inherit
 * the clock time of a button press -- and the property that mattered is
 * intact: no step is dated before the DAY somebody enrolled, so the wait in
 * days that the template asked for still happens.
 *
 * NO JITTER AT CUMULATIVE OFFSET 0 -- the rule the banner argues for, and the
 * one line in this function that is about a person rather than about dates.
 * Offset 0 is the day somebody chose when they clicked Enroll; moving it is
 * answering a question nobody asked. It is written against the running total
 * rather than against the index precisely so that an inserted, reordered or
 * hand-built step at that same offset is treated the same way.
 */
export function materializeDates(
  steps: readonly OffsetStep[],
  enrolledAt: Date,
  rand: () => number = Math.random,
): MaterializedStep[] {
  const ordered = [...steps].sort((a, b) => a.step_no - b.step_no);

  const enrolledOn = localWallClock(enrolledAt);
  const start: CivilDate = {
    year: enrolledOn.year,
    month: enrolledOn.month,
    day: enrolledOn.day,
  };

  let cumulativeDays = 0;
  // Nothing may land before the day of enrollment, so that day's business
  // instant is the floor the first step is measured against.
  let floor = dueInstantOn(start).getTime();

  return ordered.map((step) => {
    cumulativeDays += step.offset_days;
    /*
     * `<= 0` rather than `=== 0` costs nothing and cannot be reached today:
     * `enrollment_steps_offset_check` is CHECK (offset_days >= 0), so a
     * running total cannot go negative. Insurance against that constraint
     * being relaxed, not a repair -- said out loud because this directory
     * asks which of the two a guard is.
     *
     * rand is not merely ignored here, it is NOT CALLED. A caller feeding a
     * fixed sequence of jitters (the tests do) would otherwise have its
     * values consumed by a step that discards them.
     */
    const jitter = cumulativeDays <= 0 ? 0 : jitterDays(rand);
    const intendedDay = addDays(start, cumulativeDays + jitter);
    const dueMs = Math.max(dueInstantOn(intendedDay).getTime(), floor);
    floor = dueMs;
    return { step_no: step.step_no, due_at: new Date(dueMs) };
  });
}

/** A materialized step, reduced to what a resume needs to look at. */
export type PausedStep = { due_at: Date; status: string };

/**
 * Where each step falls once a paused enrollment resumes.
 *
 * EVERY PENDING STEP MOVES, NOT JUST THE NEXT ONE. Shifting only the next one
 * leaves the rest where they were, so a three-week pause on a plan of
 * 0/7/14/21 resumes into 21/14/21 -- the remaining steps bunched against each
 * other, arriving within days of one another, and the cadence the template
 * described gone. Shifting all of them by the same delta preserves every gap,
 * which is the thing the template actually specified.
 *
 * THE SHIFTED DATE IS SNAPPED, FOR THE SAME REASON THE COMPOSED ONE IS. A
 * pause is a duration in milliseconds and a resume happens whenever somebody
 * clicks; adding one to the other lands the remainder of the plan at an
 * arbitrary clock time, which is precisely the defect crm#14 is about, and it
 * would re-introduce it on every snooze that expires. Snapping also means a
 * pause and resume inside one business day moves nothing at all, which is
 * true and is what planResume() then declines to write.
 *
 * THE GAPS SURVIVE UP TO THE WEEKEND. Two steps a week apart stay a week
 * apart; two steps a day apart across a Saturday and a Sunday both land on
 * the Monday. That is the same trade the composer makes and it is the right
 * way round: a plan whose gaps are perfect but whose steps fall at a weekend
 * is a plan nobody works.
 *
 * `generated` AND `skipped` ARE HISTORY AND DO NOT MOVE -- AND ARE NOT
 * SNAPPED EITHER. A generated step has already written a task; moving its
 * date makes the step disagree with the task it produced, and this schema
 * gives every fact exactly one writer for precisely that reason. A skipped
 * step carries a reason somebody wrote on a particular day, and redating it
 * makes that note describe a day nobody wrote it on. Snapping is a redating,
 * so history is handed back byte for byte.
 *
 * A NEGATIVE DELTA IS REFUSED RATHER THAN APPLIED. `resumedAt` before
 * `pausedAt` is a wrong clock or a swapped argument, and honoring it would
 * pull pending steps EARLIER -- silently making a paused enrollment more
 * urgent than it was before anybody paused it.
 *
 * Returns one date per step, in the order given, so the caller can pair them
 * back up with the rows it read. Statuses outside 'pending' are returned
 * unchanged rather than rejected: the column is the database's to grow, and a
 * fourth value this file has not been told about must not be silently moved.
 */
export function shiftAfterPause(
  steps: readonly PausedStep[],
  pausedAt: Date,
  resumedAt: Date,
): Date[] {
  const delta = Math.max(0, resumedAt.getTime() - pausedAt.getTime());
  return steps.map((step) =>
    step.status === "pending"
      ? businessDueAt(new Date(step.due_at.getTime() + delta))
      : new Date(step.due_at.getTime()),
  );
}
